// frontend/src/pages/PortfolioMember.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { 
  Calendar, 
  MapPin, 
  Mail, 
  Globe, 
  Github, 
  Linkedin, 
  Twitter, 
  Instagram,
  Briefcase,
  Award,
  BookOpen,
  ExternalLink
} from 'lucide-react';

const PortfolioMember = () => {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    fetchMember();
  }, [id]);

  const fetchMember = async () => {
    try {
      const response = await apiService.getTeamMember(id);
      setMember(response.data);
    } catch (error) {
      console.error('Error fetching team member:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-white mb-2">عضو تیم یافت نشد</h2>
          <Link to="/portfolio" className="text-pink-400 hover:text-pink-300">
            بازگشت به صفحه تیم
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header Section */}
        <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 mb-8">
          {/* Cover Image */}
          {member.cover_image && (
            <div className="h-48 md:h-64 relative">
              <img
                src={member.cover_image}
                alt={`Cover ${member.full_name}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
            </div>
          )}

          {/* Profile Info */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-6 md:space-y-0 md:space-x-6 md:space-x-reverse">
              {/* Profile Image */}
              <div className="relative -mt-20 md:-mt-24">
                <img
                  src={member.profile_image}
                  alt={member.full_name}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-gray-900 object-cover"
                />
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {member.full_name}
                </h1>
                <p className="text-xl text-pink-400 font-medium mb-4 capitalize">
                  {member.position}
                </p>
                
                {/* Contact Info */}
                <div className="flex flex-wrap gap-4 text-gray-300 mb-4">
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="flex items-center space-x-2 space-x-reverse hover:text-white transition-colors">
                      <Mail size={16} />
                      <span>ایمیل</span>
                    </a>
                  )}
                  {member.website && (
                    <a href={member.website} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 space-x-reverse hover:text-white transition-colors">
                      <Globe size={16} />
                      <span>وبسایت</span>
                    </a>
                  )}
                </div>

                {/* Social Links */}
                <div className="flex space-x-3 space-x-reverse">
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors">
                      <Linkedin size={20} />
                    </a>
                  )}
                  {member.github && (
                    <a href={member.github} target="_blank" rel="noopener noreferrer" className="bg-gray-700 text-white p-2 rounded-lg hover:bg-gray-600 transition-colors">
                      <Github size={20} />
                    </a>
                  )}
                  {member.twitter && (
                    <a href={member.twitter} target="_blank" rel="noopener noreferrer" className="bg-sky-500 text-white p-2 rounded-lg hover:bg-sky-600 transition-colors">
                      <Twitter size={20} />
                    </a>
                  )}
                  {member.instagram && (
                    <a href={member.instagram} target="_blank" rel="noopener noreferrer" className="bg-pink-600 text-white p-2 rounded-lg hover:bg-pink-700 transition-colors">
                      <Instagram size={20} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 mb-8">
          {/* Tab Navigation */}
          <div className="border-b border-gray-800">
            <nav className="flex space-x-2 space-x-reverse px-6">
              {[
                { id: 'about', label: 'درباره', icon: BookOpen },
                { id: 'projects', label: 'پروژه‌ها', icon: Briefcase },
                { id: 'experience', label: 'سوابق', icon: Award },
                { id: 'education', label: 'تحصیلات', icon: Award },
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 space-x-reverse py-4 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-pink-500 text-pink-400'
                        : 'border-transparent text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'about' && <AboutTab member={member} />}
            {activeTab === 'projects' && <ProjectsTab projects={member.projects} />}
            {activeTab === 'experience' && <ExperienceTab experiences={member.experiences} />}
            {activeTab === 'education' && <EducationTab educations={member.educations} />}
          </div>
        </div>
      </div>
    </div>
  );
};

// کامپوننت‌های تب‌ها
const AboutTab = ({ member }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-xl font-bold text-white mb-4">بیوگرافی</h3>
      <div 
        className="text-gray-300 leading-8 prose prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: member.bio }}
      />
    </div>

    {member.skills_list.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-white mb-4">مهارت‌ها</h3>
        <div className="flex flex-wrap gap-2">
          {member.skills_list.map((skill, index) => (
            <span
              key={index}
              className="bg-gray-800 text-gray-300 px-3 py-2 rounded-lg text-sm"
            >
              {skill}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const ProjectsTab = ({ projects }) => (
  <div>
    <h3 className="text-xl font-bold text-white mb-6">پروژه‌ها ({projects.length})</h3>
    {projects.length === 0 ? (
      <p className="text-gray-400 text-center py-8">هنوز پروژه‌ای ثبت نشده است.</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map(project => (
          <div key={project.id} className="bg-gray-800 rounded-xl p-5 border border-gray-700">
            <img
              src={project.featured_image}
              alt={project.title}
              className="w-full h-40 object-cover rounded-lg mb-4"
            />
            <h4 className="text-lg font-bold text-white mb-2">{project.title}</h4>
            <p className="text-gray-300 text-sm mb-4 line-clamp-3">{project.short_description}</p>
            
            {project.technologies_list.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {project.technologies_list.slice(0, 3).map((tech, index) => (
                  <span key={index} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs">
                    {tech}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex space-x-3 space-x-reverse">
                {project.project_url && (
                  <a
                    href={project.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-400 hover:text-pink-300"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                {project.github_url && (
                  <a
                    href={project.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white"
                  >
                    <Github size={16} />
                  </a>
                )}
              </div>
              <span className="text-gray-400 text-sm">
                {project.is_ongoing ? 'در حال انجام' : 'تکمیل شده'}
              </span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ExperienceTab = ({ experiences }) => (
  <div>
    <h3 className="text-xl font-bold text-white mb-6">سوابق کاری ({experiences.length})</h3>
    {experiences.length === 0 ? (
      <p className="text-gray-400 text-center py-8">هنوز سابقه کاری ثبت نشده است.</p>
    ) : (
      <div className="space-y-6">
        {experiences.map(exp => (
          <div key={exp.id} className="flex space-x-4 space-x-reverse">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
              <div className="w-0.5 h-full bg-gray-700 mt-2"></div>
            </div>
            <div className="flex-1 pb-6">
              <h4 className="text-lg font-bold text-white">{exp.position}</h4>
              <p className="text-pink-400 mb-2">{exp.company}</p>
              <p className="text-gray-300 text-sm mb-2">{exp.description}</p>
              <div className="flex items-center space-x-4 space-x-reverse text-gray-400 text-sm">
                <span>
                  {new Date(exp.start_date).toLocaleDateString('fa-IR')} -{' '}
                  {exp.is_current ? 'اکنون' : new Date(exp.end_date).toLocaleDateString('fa-IR')}
                </span>
                <span>•</span>
                <span>{exp.duration}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const EducationTab = ({ educations }) => (
  <div>
    <h3 className="text-xl font-bold text-white mb-6">تحصیلات ({educations.length})</h3>
    {educations.length === 0 ? (
      <p className="text-gray-400 text-center py-8">هنوز تحصیلات ثبت نشده است.</p>
    ) : (
      <div className="space-y-6">
        {educations.map(edu => (
          <div key={edu.id} className="flex space-x-4 space-x-reverse">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <div className="w-0.5 h-full bg-gray-700 mt-2"></div>
            </div>
            <div className="flex-1 pb-6">
              <h4 className="text-lg font-bold text-white">{edu.field}</h4>
              <p className="text-blue-400 mb-2">{edu.university}</p>
              <p className="text-gray-300 text-sm mb-2 capitalize">{edu.degree_display}</p>
              <div className="text-gray-400 text-sm">
                {new Date(edu.start_date).toLocaleDateString('fa-IR')} -{' '}
                {edu.is_current ? 'اکنون' : new Date(edu.end_date).toLocaleDateString('fa-IR')}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default PortfolioMember;